'use client';

import type { Post, PostCategory, PostTag } from '@routeroyal/theme-core';
import {
  AllysPostFilters,
  AllysPostGrid,
  AllysPostHero,
} from '@routeroyal/theme-allys';
import { useMemo, useState } from 'react';

type Props = {
  locale: string;
  messages: Record<string, unknown>;
  posts: Post[];
  categories: PostCategory[];
  tags: PostTag[];
  basePath?: string;
};

/**
 * Filter and sort posts based on user selections
 */
function filterAndSortPosts(
  posts: Post[],
  options: {
    categorySlug?: string;
    tagSlugs?: string[];
    searchQuery?: string;
    sortBy?: 'latest' | 'oldest' | 'popular';
  }
): Post[] {
  let filtered = [...posts];

  // Filter by category
  if (options.categorySlug) {
    filtered = filtered.filter(post => post.category?.slug === options.categorySlug);
  }

  // Filter by tags
  if (options.tagSlugs && options.tagSlugs.length > 0) {
    filtered = filtered.filter(post =>
      post.tags?.some(tag => options.tagSlugs!.includes(tag.slug))
    );
  }

  // Filter by search query
  if (options.searchQuery) {
    const query = options.searchQuery.toLowerCase();
    filtered = filtered.filter(post =>
      post.title.toLowerCase().includes(query) ||
      post.excerpt?.toLowerCase().includes(query) ||
      post.content?.toLowerCase().includes(query)
    );
  }

  // Sort
  if (options.sortBy === 'oldest') {
    filtered.sort((a, b) => {
      const dateA = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
      const dateB = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
      return dateA - dateB;
    });
  } else if (options.sortBy === 'popular') {
    // Sort by view count if available
    filtered.sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0));
  } else {
    // 'latest' is default
    filtered.sort((a, b) => {
      const dateA = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
      const dateB = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
      return dateB - dateA;
    });
  }

  return filtered;
}

export function BlogListingContentAllys({ 
  locale, 
  messages, 
  posts, 
  categories, 
  tags,
  basePath = '/news',
}: Props) {
  const blogMessages = (messages.blog || {}) as Record<string, unknown>;
  
  // Helper function to get nested translation values
  const t = (key: string): string => {
    const keys = key.split('.');
    let value: unknown = blogMessages;
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = (value as Record<string, unknown>)[k];
      } else {
        // Fallback to English defaults
        const defaults: Record<string, string> = {
          'hero.title': 'News & Insights',
          'hero.subtitle': 'Stay updated with the latest news, insights, and stories from our team',
          'filters.category': 'Category',
          'filters.allCategories': 'All Categories',
          'filters.sortBy': 'Sort by',
          'filters.latest': 'Latest',
          'filters.oldest': 'Oldest',
          'filters.popular': 'Most Popular',
          'empty.title': 'No articles found',
          'empty.description': 'Try adjusting your search or filter criteria',
        };
        return defaults[key] || key;
      }
    }
    
    return typeof value === 'string' ? value : key;
  };
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>();
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<'latest' | 'oldest' | 'popular'>('latest');

  const filteredPosts = useMemo(() => {
    const filterOptions: {
      categorySlug?: string;
      tagSlugs?: string[];
      searchQuery?: string;
      sortBy?: 'latest' | 'oldest' | 'popular';
    } = { sortBy };
    if (selectedCategory) filterOptions.categorySlug = selectedCategory;
    if (selectedTags.length > 0) filterOptions.tagSlugs = selectedTags;
    if (searchQuery) filterOptions.searchQuery = searchQuery;
    return filterAndSortPosts(posts, filterOptions);
  }, [posts, selectedCategory, selectedTags, searchQuery, sortBy]);

  const sortOptions = [
    { label: t('filters.latest'), value: 'latest' },
    { label: t('filters.oldest'), value: 'oldest' },
    { label: t('filters.popular'), value: 'popular' },
  ];

  // Handle category change
  const handleCategoryChange = (categorySlug: string | undefined) => {
    setSelectedCategory(categorySlug);
  };

  // Handle tag change
  const handleTagsChange = (tagSlugs: string[]) => {
    setSelectedTags(tagSlugs);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <AllysPostHero
        title={t('hero.title')}
        subtitle={t('hero.subtitle')}
        backgroundImage="https://media.routeroyal.com/rHhIrx29sn05vdWMRaePRO-sBFc82JHDWiCXeW4R9nU/raw:1/czM6Ly9jb2xsYXBzaWJsZS1wdXJzZS1kbW9rLWRubDd4cy9yb3V0ZXJveWFsL3JveWFsZS9zdGF0aWMvYWRjNWJkZDgxYzk1N2NkODlmMGU3YTY3ZDE0OTAwMjk3YmViMzFhYzNjODRlNWQzMmQ5ZDcwOWE3YTYwNWQzNC5wbmc.png"
        showSearch={true}
        onSearch={setSearchQuery}
        searchPlaceholder="Search articles..."
      />

      {/* Filters Section */}
      <section className="py-6 sm:py-10 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AllysPostFilters
            categories={categories}
            selectedCategory={selectedCategory ?? ''}
            onCategoryChange={handleCategoryChange}
            tags={tags}
            selectedTags={selectedTags}
            onTagsChange={handleTagsChange}
            sortOptions={sortOptions}
            sortBy={sortBy}
            onSortChange={(value) => setSortBy(value as 'latest' | 'oldest' | 'popular')}
            messages={{
              filterByCategory: t('filters.category'),
              allCategories: 'All',
              sortBy: 'Sort',
              filterByTag: 'Tags',
            }}
          />
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-8 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Results count */}
          <div className="mb-8 text-gray-600 [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]">
            {filteredPosts.length === 0 ? (
              <span>{t('empty.title')}</span>
            ) : filteredPosts.length === 1 ? (
              <span>1 article found</span>
            ) : (
              <span>{filteredPosts.length} articles found</span>
            )}
          </div>
          
          {filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="text-2xl font-bold text-[#243462] mb-4 [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]">
                {t('empty.title')}
              </h3>
              <p className="text-gray-600 [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]">
                {t('empty.description')}
              </p>
            </div>
          ) : (
            <AllysPostGrid
              posts={filteredPosts}
              variant="grid"
              columns={3}
              emptyMessage={t('empty.title')}
              locale={locale}
              basePath={basePath}
            />
          )}
        </div>
      </section>
    </div>
  );
}
